import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import I18n from "i18n-js";
import { Dialog } from "@mui/material";
import { useDialog } from "app/hook";
import { RootState } from "app/store";
import { DialogType } from "app/types";
import { actions } from "app/configSlice";
import { btnDelay, useScale, usePlaySound } from "common/utils";
import { CloseBtn, PlanBadge, FadeTransition } from "common/elements";
import { BillingType } from "features/user/types";
import { ParentGate, ParentWarningTitle } from "features/parent";

const ComparisonRow = ({
  icon,
  label,
  trialValue,
  freeValue,
  isLast = false,
  useIconValues = false,
}: {
  icon: string;
  label: string;
  trialValue: string;
  freeValue: string;
  isLast?: boolean;
  useIconValues?: boolean;
}) => (
  <div
    className={`grid ${!isLast ? "border-b" : ""}`}
    style={{
      gridTemplateColumns: "45% 27.5% 27.5%",
      ...(!isLast ? { borderBottomColor: "#E8DCC8" } : {}),
    }}
  >
    <div className="flex items-center gap-2 p-4 text text-textcolor-black">
      <span className="material-symbols-outlined !text-2xl text-gray2-100">
        {icon}
      </span>
      {label}
    </div>
    <div className="flex items-center justify-center p-4 text text-gray2-100 text-center">
      {useIconValues ? (
        <span className="material-symbols-outlined !text-3xl">close</span>
      ) : (
        trialValue
      )}
    </div>
    <div
      className={`flex items-center justify-center p-4 text text-textcolor-black text-center border-l-4 border-r-4 bg-primary-10 ${isLast ? "border-b-4" : ""}`}
      style={{
        borderLeftColor: "#4A90E2",
        borderRightColor: "#4A90E2",
        borderBottomColor: isLast ? "#4A90E2" : undefined,
      }}
    >
      {useIconValues ? (
        <span className="material-symbols-outlined !text-3xl">circle</span>
      ) : (
        freeValue
      )}
    </div>
  </div>
);

export const AccountPromotion = () => {
  const PANEL_WIDTH = 1180;
  const PANEL_HEIGHT = 900;
  const play = usePlaySound();
  const { scale } = useScale();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const handleDialog = useDialog();
  const [parentGate, setParentGate] = useState(false);
  const accountPromotion = useSelector(
    (state: RootState) => state.config.dialog.account_promotion,
  );
  const handleClose = () => {
    btnDelay(() => {
      play();
      handleDialog({ type: DialogType.ACCOUNT_PROMOTION, value: false });
    });
  };

  const handleSignUp = () => {
    btnDelay(() => {
      play();
      setParentGate(true);
    });
  };

  const handleParentGateClose = () => setParentGate(false);
  const handleParentGateConfirm = () => {
    setParentGate(false);
    dispatch(actions.clearDialog());
    navigate("/auth/register");
  };

  return (
    <>
      <Dialog
        fullScreen
        maxWidth={false}
        sx={{ zIndex: 999999999 }}
        open={accountPromotion.value}
        TransitionComponent={FadeTransition}
        componentsProps={{
          backdrop: { style: { backgroundColor: "transparent" } },
        }}
        PaperProps={{
          style: {
            margin: 0,
            overflow: "visible",
            backgroundColor: "transparent",
          },
          elevation: 0,
        }}
      >
        <div
          className="flex-col-el flex-center relative w-full h-full"
          style={{
            filter:
              "drop-shadow(0px 6px 6px rgb(0 0 0 / 20%)) drop-shadow(0px 10px 14px rgb(0 0 0 / 14%)) drop-shadow(0px 4px 18px rgb(0 0 0 / 12%))",
          }}
        >
          <div
            className="flex-col-el flex-center"
            style={{
              width: PANEL_WIDTH * scale,
              height: PANEL_HEIGHT * scale,
            }}
          >
            <div
              className="flex-col-el flex-center origin-center"
              style={{
                width: PANEL_WIDTH,
                height: PANEL_HEIGHT,
                transform: `scale(${scale})`,
                marginTop: 32 * scale,
              }}
            >
              <CloseBtn close={handleClose} />
              <div className="flex-col-el flex-center w-full h-full rounded-[8px] bg-beige-60 p-6 shadow-[0_12px_40px_rgba(63,74,97,0.12),0_4px_12px_rgba(63,74,97,0.08)] border-[8px] border-solid border-white relative overflow-hidden">
                <ParentWarningTitle />

                <div className="flex-col-el flex-center pb-6">
                  <p className="text-body text-textcolor-black text-2xl text-center">
                    {I18n.t("MSG_ACCOUNT_PROMOTION_TITLE")}
                  </p>
                </div>

                <div className="flex-col-el w-full h-full">
                  <div className="px-10 pb-2">
                    <div
                      className="bg-white overflow-hidden"
                      style={{
                        boxShadow:
                          "-1px 1px 1px rgba(63,74,97,0.10), 1px 1px 3px rgba(63,74,97,0.15)",
                      }}
                    >
                      {/* Table Header */}
                      <div
                        className="grid bg-beige-40 border-b-2 border-gray2-40"
                        style={{ gridTemplateColumns: "45% 27.5% 27.5%" }}
                      >
                        <div className="p-4"></div>
                        <div className="flex items-center justify-center p-4 text-center">
                          <p className="text text-textcolor-black">
                            {I18n.t(
                              "MSG_ACCOUNT_PROMOTION_TABLE_CURRENT_LABEL",
                            )}
                          </p>
                        </div>
                        <div
                          className="flex items-center justify-center p-4 text-center border-l-4 border-r-4 border-t-4 bg-primary-10"
                          style={{
                            borderLeftColor: "#4A90E2",
                            borderRightColor: "#4A90E2",
                            borderTopColor: "#4A90E2",
                          }}
                        >
                          <p className="text text-textcolor-black">
                            {I18n.t(
                              "MSG_ACCOUNT_PROMOTION_TABLE_RECOMMENDED_LABEL",
                            )}
                          </p>
                        </div>
                      </div>

                      {/* Table Rows */}
                      {/* Plan Badge Row */}
                      <div
                        className="grid border-b"
                        style={{
                          gridTemplateColumns: "45% 27.5% 27.5%",
                          borderBottomColor: "#E8DCC8",
                        }}
                      >
                        <div className="flex items-center gap-2 p-4 text text-textcolor-black">
                          <span className="material-symbols-outlined !text-2xl text-gray2-100">
                            workspace_premium
                          </span>
                          プラン
                        </div>
                        <div className="flex items-center justify-center p-4">
                          <PlanBadge plan={BillingType.TRIAL} />
                        </div>
                        <div
                          className="flex items-center justify-center p-4 border-l-4 border-r-4 bg-primary-10"
                          style={{
                            borderLeftColor: "#4A90E2",
                            borderRightColor: "#4A90E2",
                          }}
                        >
                          <PlanBadge plan={BillingType.FREE} />
                        </div>
                      </div>
                      <ComparisonRow
                        icon="group"
                        label={I18n.t("MSG_ACCOUNT_PROMOTION_TABLE_USER_COUNT")}
                        trialValue={I18n.t(
                          "MSG_ACCOUNT_PROMOTION_TABLE_USER_TRIAL",
                        )}
                        freeValue={I18n.t(
                          "MSG_ACCOUNT_PROMOTION_TABLE_USER_FREE",
                        )}
                      />
                      <ComparisonRow
                        icon="construction"
                        label={I18n.t(
                          "MSG_ACCOUNT_PROMOTION_TABLE_CREATOR_COUNT",
                        )}
                        trialValue={I18n.t(
                          "MSG_ACCOUNT_PROMOTION_TABLE_CREATOR_TRIAL",
                        )}
                        freeValue={I18n.t(
                          "MSG_ACCOUNT_PROMOTION_TABLE_CREATOR_FREE",
                        )}
                      />
                      <ComparisonRow
                        icon="school"
                        label={I18n.t(
                          "MSG_ACCOUNT_PROMOTION_TABLE_COURSE_ACCESS",
                        )}
                        trialValue={I18n.t(
                          "MSG_ACCOUNT_PROMOTION_TABLE_COURSE_TRIAL",
                        )}
                        freeValue={I18n.t(
                          "MSG_ACCOUNT_PROMOTION_TABLE_COURSE_FREE",
                        )}
                      />
                      <ComparisonRow
                        icon="sync"
                        label={I18n.t("MSG_ACCOUNT_PROMOTION_TABLE_SYNC")}
                        trialValue={I18n.t(
                          "MSG_ACCOUNT_PROMOTION_TABLE_SYNC_TRIAL",
                        )}
                        freeValue={I18n.t(
                          "MSG_ACCOUNT_PROMOTION_TABLE_SYNC_FREE",
                        )}
                        isLast={true}
                        useIconValues={true}
                      />
                    </div>
                  </div>

                  <div className="flex-row-el flex-center gap-6 px-10 alpha">
                    <button
                      className="btn btn-blue"
                      onClick={handleClose}
                      aria-label={I18n.t("MSG_ACCOUNT_PROMOTION_LATER_BTN")}
                    >
                      <div className="flex-row-center gap-1 mb-[3px]">
                        <span className="material-symbols-outlined !text-4xl text-white">
                          close
                        </span>
                        <p className="text-body text-white text-font-size-body1">
                          {I18n.t("MSG_ACCOUNT_PROMOTION_LATER_BTN")}
                        </p>
                      </div>
                    </button>

                    <button
                      className="btn btn-primary"
                      onClick={handleSignUp}
                      aria-label={I18n.t("MSG_ACCOUNT_PROMOTION_SIGNUP_BTN")}
                    >
                      <div className="flex-row-center gap-1 mb-[3px]">
                        <span className="material-symbols-outlined !text-4xl text-white">
                          person_add
                        </span>
                        <p className="text-body text-white text-font-size-body1">
                          {I18n.t("MSG_ACCOUNT_PROMOTION_SIGNUP_BTN")}
                        </p>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Dialog>

      <ParentGate
        visible={parentGate}
        confirm={handleParentGateConfirm}
        close={handleParentGateClose}
      />
    </>
  );
};
